// src/lib/streamer-client.js
import { sendChunk, sendStop } from '$lib/websocket-client.js';
import ApiClient from '$lib/api';
const api = new ApiClient();

import { sendMessage, conversations, conversationId, myId } from '$lib/websocket-client';
import { get } from 'svelte/store';
import { playRecording, playShutter, playStopRecording } from '$lib/audio.js';
import { openCamera, stopCameraStream } from '@components/cameras/camera-device.js';
import { captureImage as captureCameraImage, toAbsoluteMediaUrl } from '@components/cameras/capture-image.js';
import { countPendingChunks } from '@components/cameras/recording-queue.js';
import { enableRecordingRecovery, syncPendingRecordings } from '@components/cameras/recording-sync.js';
import { createResilientRecorder } from '@components/cameras/resilient-recorder.js';
import { createCameraStreamClient } from '$lib/streamClient.js';

function getMyCloudConversationId() {
    const convStore = get(conversations) || [];
    return convStore.find(c => c.type === 'cloud')?.conversationId || null;
}

export function sendRecordingToMyCloud(recording) {
    const cloudConversationId = getMyCloudConversationId();

    if (!cloudConversationId || !recording?.outputUrl) {
        return false;
    }

    const extension = recording.outputFile?.split('.').pop() || 'webm';
    const mimeByExtension = {
        mp4: 'video/mp4',
        webm: 'video/webm',
        mkv: 'video/x-matroska',
        mov: 'video/quicktime'
    };

    return sendMessage({
        type: 'chat',
        conversationId: cloudConversationId,
        from: get(myId),
        message: recording.outputUrl,
        msgType: 'media',
        meta: {
            mediaType: 'video',
            name: recording.outputFile || `recording.${extension}`,
            size: recording.outputBytes || null,
            mime: mimeByExtension[extension] || 'video/webm'
        }
    });
}

export async function postImage(blob, {
    convId = null,
    toUserId = null
} = {}) {

    let convIdFinal = convId;

    if (!convIdFinal) {
        convIdFinal = getMyCloudConversationId();

        if (convIdFinal) {
            console.log("📦 to MyCloud:", convIdFinal);
        }
    }

    const form = new FormData();
    if (convIdFinal) {
        form.append("conversation_id", convIdFinal);
    }
    form.append("file", blob, "capture.jpg");

    const uploaded = await api.post("/api/upload", form, {
        withCredentials: true,
        responseType: "json"
    });

    if (!convIdFinal) {
        console.error("❌ No conversationId and no MyCloud found");
        return uploaded;
    }

    const msg = {
        type: 'chat',
        conversationId: toUserId ? null : convIdFinal,
        from: get(myId),

        message: uploaded.url,
        msgType: 'media',

        meta: {
            mediaType: uploaded.type,
            name: uploaded.name,
            size: uploaded.size,
            mime: uploaded.mime
        }
    };

    sendMessage(msg);

    return uploaded;
}

export async function uploadVideo(blob, {
    convId = null,
    toUserId = null
} = {}) {

    try {
        let convIdFinal = convId;

        if (!convIdFinal) {
            convIdFinal = getMyCloudConversationId();

            if (convIdFinal) {
                console.log("📦 Video saved to MyCloud:", convIdFinal);
            }
        }

        const form = new FormData();
        if (convIdFinal) {
            form.append("conversation_id", convIdFinal);
        }
        form.append("file", blob, "record.webm");

        const uploaded = await api.post("/api/upload", form, {
            withCredentials: true,
            responseType: "json"
        });

        if (!convIdFinal) {
            console.error("❌ No conversationId and no MyCloud found");
            return;
        }

        const msg = {
            type: 'chat',
            conversationId: toUserId || convIdFinal,
            from: get(myId),

            message: uploaded.url,
            msgType: 'media',

            meta: {
                mediaType: 'video',
                name: uploaded.name,
                size: uploaded.size,
                mime: uploaded.mime
            }
        };

        sendMessage(msg);

        return uploaded;

    } catch (err) {
        console.error("❌ uploadVideo error:", err);
        throw err;
    }
}


/* =========================================================
   🎥 CAMERA
========================================================= */
export async function startCamera(videoEl) {
    const stream = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: true
    });

    videoEl.srcObject = stream;
    return stream;
}

export function stopCamera(stream) {
    stream?.getTracks().forEach(t => t.stop());
}

/* =========================================================
   📸 CAPTURE IMAGE
========================================================= */
export function captureImage(videoEl) {
    const canvas = document.createElement("canvas");
    canvas.width = videoEl.videoWidth;
    canvas.height = videoEl.videoHeight;

    const ctx = canvas.getContext("2d");
    ctx.drawImage(videoEl, 0, 0);

    return new Promise(resolve =>
        canvas.toBlob(resolve, "image/jpeg")
    );
}

export async function uploadCapturedImage(videoEl) {
    const blob = await captureImage(videoEl);
    return postImage(blob);
}

/* =========================================================
   🎬 RECORDER (HYBRID: RECORD + STREAM)
========================================================= */
export function createRecorder(stream, {
    enableStream = true,
    chunkInterval = 500,
    onChunk,
    onStop
} = {}) {

    let chunks = [];

    const recorder = new MediaRecorder(stream, {
        mimeType: 'video/webm;codecs=vp8'
    });

    recorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
            chunks.push(e.data);

            // 👉 callback UI
            onChunk?.(e.data);

            // 👉 stream realtime
            if (enableStream) {
                streamChunk(e.data);
            }
        }
    };

    recorder.onstop = async () => {
        const blob = new Blob(chunks, { type: 'video/webm' });

        // 👉 callback UI
        await onStop?.(blob);

        // 👉 stop stream
        if (enableStream) {
            stopStream();
        }

        chunks = [];
    };

    return {
        start() {
            recorder.start(chunkInterval);
        },
        stop() {
            recorder.stop();
        },
        instance: recorder
    };
}

/* =========================================================
   📡 STREAM (WEBSOCKET)
========================================================= */
export function streamChunk(blob) {
    blob.arrayBuffer().then(buffer => {
        sendChunk(buffer);
    });
}

export function stopStream() {
    sendStop();
}


/* =========================================================
   🎯 FULL FLOW HELPER (QUAN TRỌNG)
========================================================= */
export async function startRecordingFlow(videoEl, {
    enableStream = true,
    onUploaded,
    onChunk
} = {}) {

    const stream = await startCamera(videoEl);

    const recorder = createRecorder(stream, {
        enableStream,
        onChunk,
        onStop: async (blob) => {
            const file = await uploadVideo(blob);

            // 👉 callback khi upload xong
            onUploaded?.(file);
        }
    });

    recorder.start();

    return {
        stop() {
            recorder.stop();
            stopCamera(stream);
        },
        stream,
        recorder
    };
}

// 080526
export function createRecorderChunk(
    stream,
    {
        chunkInterval = 1000,
        sessionId = crypto.randomUUID(),

        onChunkUploaded,
        onStop
    } = {}
) {

    let chunkIndex = 0;

    const recorder = new MediaRecorder(stream, {
        mimeType: "video/webm;codecs=vp8"
    });

    recorder.ondataavailable = async ({ data }) => {

        if (!data || data.size <= 0) return;

        try {

            await api.post(
                "/api/record/chunk",
                data,
                {
                    withCredentials: true,

                    headers: {
                        "Content-Type": "video/webm",
                        "x-session-id": sessionId,
                        "x-chunk-index": chunkIndex
                    }
                }
            );

            onChunkUploaded?.(chunkIndex);

            chunkIndex++;

        } catch (err) {

            console.error("❌ upload chunk error:", err);
        }
    };

    recorder.onstop = async () => {

        try {

            await api.post(
                "/api/record/finish",
                {
                    sessionId
                },
                {
                    withCredentials: true
                }
            );

            onStop?.();

        } catch (err) {

            console.error("❌ finish record error:", err);
        }
    };

    return {

        sessionId,

        start() {
            recorder.start(chunkInterval);
        },

        stop() {
            recorder.stop();
        },

        instance: recorder
    };
}

/* =========================================================
   CAMVIEW REFACTOR HELPERS
   Ghi chu: nhom helper nay duoc tach tu CamView.svelte
   de component de bao tri hon, khong doi logic hien tai.
========================================================= */
export function createCameraNetworkStateHandler(setOnline) {
    return () => {
        setOnline(navigator.onLine);
    };
}

export function setupCameraRecovery(handleSyncProgress, refreshPendingCount, initCamera) {
    const recoveryCleanup = enableRecordingRecovery(handleSyncProgress);
    refreshPendingCount();
    initCamera();

    return recoveryCleanup;
}

export async function refreshCameraPendingCount(setPendingChunks) {
    try {
        const pendingChunks = await countPendingChunks();
        setPendingChunks(pendingChunks);
        return pendingChunks;
    } catch (error) {
        console.warn('Khong doc duoc hang doi camera', error);
        return null;
    }
}

export function createCameraSyncProgressHandler({
    getOnline,
    getRecording,
    setRecordingStatus,
    refreshPendingCount
}) {
    return (event) => {
        if (event.type === 'completed') {
            setRecordingStatus('Đã lưu stream vào My Cloud');
        } else if (event.type === 'sync-error') {
            setRecordingStatus(getOnline() ? 'Lỗi đồng bộ, hệ thống sẽ thử lại' : 'Mất mạng, đang lưu trên thiết bị');
        } else if (event.type === 'chunk-uploaded') {
            setRecordingStatus(getRecording() ? 'Đang quay và đồng bộ' : 'Đang đồng bộ');
        }

        refreshPendingCount();
    };
}

export async function initializeCameraView({
    mediaStream,
    videoElement,
    facingMode,
    cameraConfig,
    setRecordingStatus
}) {
    try {
        stopCameraStream(mediaStream, videoElement);
        const nextMediaStream = await openCamera(videoElement, facingMode, cameraConfig?.capture);
        setRecordingStatus('Sẵn sàng');
        return nextMediaStream;
    } catch (error) {
        setRecordingStatus(error.message);
        console.error('Khong mo duoc camera', error);
        return null;
    }
}

export async function toggleCameraFacingMode({
    recording,
    stopping,
    facingMode,
    initCamera
}) {
    if (recording || stopping) return facingMode;

    const nextFacingMode = facingMode === 'user' ? 'environment' : 'user';
    await initCamera(nextFacingMode);
    return nextFacingMode;
}

export function stopCameraViewStream(mediaStream, videoElement) {
    stopCameraStream(mediaStream, videoElement);
    return null;
}

export function updateCameraCaptureList(captures, id, changes) {
    return captures.map((capture) => (capture.id === id ? { ...capture, ...changes } : capture));
}

export async function captureCameraFrame({
    videoElement,
    captures,
    setRecordingStatus
}) {
    try {
        playShutter();
        const blob = await captureCameraImage(videoElement);
        const id = crypto.randomUUID();
        const localUrl = URL.createObjectURL(blob);

        let nextCaptures = [
            {
                id,
                url: localUrl,
                localUrl,
                status: 'uploading',
                createdAt: new Date().toISOString()
            },
            ...captures
        ];

        const uploaded = await postImage(blob);

        if (uploaded?.url) {
            nextCaptures = updateCameraCaptureList(nextCaptures, id, {
                url: toAbsoluteMediaUrl(uploaded.url),
                status: 'uploaded'
            });
        } else {
            nextCaptures = updateCameraCaptureList(nextCaptures, id, { status: 'error' });
        }

        return nextCaptures;
    } catch (error) {
        console.error('Chup anh that bai', error);
        setRecordingStatus(error.message);
        return captures;
    }
}

export async function startCameraStreamRecording({
    mediaStream,
    cameraConfig,
    initCamera,
    getOnline,
    refreshPendingCount,
    handleSyncProgress,
    setRecordingStatus
}) {
    try {
        let nextMediaStream = mediaStream;

        if (!nextMediaStream) {
            nextMediaStream = await initCamera();
        }

        if (!nextMediaStream) {
            return { mediaStream: nextMediaStream, recorderController: null };
        }

        playRecording();
        const recordingConfig = cameraConfig?.recording || {};
        const createHttpRecorder = () => createResilientRecorder(nextMediaStream, {
            segmentDuration: recordingConfig.segmentDurationMs || 5000,
            preferredMimeType: recordingConfig.preferredMimeType || '',
            videoBitsPerSecond: recordingConfig.videoBitsPerSecond || null,
            audioBitsPerSecond: recordingConfig.audioBitsPerSecond || null,
            capture: cameraConfig?.capture || null,
            output: cameraConfig?.output || null,
            ffmpeg: cameraConfig?.ffmpeg || null,
            onChunkQueued: () => {
                setRecordingStatus(getOnline() ? 'Đang quay và gửi stream' : 'Mất mạng, đang lưu trên thiết bị');
                refreshPendingCount();
            },
            onSyncProgress: handleSyncProgress,
            onStateChange: (state) => {
                if (state === 'recording') setRecordingStatus('Đang quay');
                if (state === 'syncing') setRecordingStatus('Đang gửi các đoạn còn lại');
            }
        });

        let recorderController;
        if (recordingConfig.transportMode !== 'http-chunk') {
            const streamClient = createCameraStreamClient({
                onStatus: setRecordingStatus,
                onError: (error) => console.error('Camera WebSocket stream error', error),
                onChunk: () => setRecordingStatus('Đang quay · Luồng 2 WebSocket')
            });

            try {
                await streamClient.start({
                    mediaStream: nextMediaStream,
                    userId: get(myId) || 0,
                    cameraName: 'K2 Camera',
                    outputType: ['mp4', 'webm'].includes(cameraConfig?.output?.format)
                        ? cameraConfig.output.format
                        : 'mp4',
                    recordingStream: 2,
                    hasAudio: nextMediaStream.getAudioTracks().length > 0,
                    hasVideo: nextMediaStream.getVideoTracks().length > 0,
                    preferredMimeType: recordingConfig.preferredMimeType || '',
                    chunkIntervalMs: recordingConfig.segmentDurationMs || 3000,
                    videoBitsPerSecond: recordingConfig.videoBitsPerSecond || null,
                    audioBitsPerSecond: recordingConfig.audioBitsPerSecond || null,
                    ffmpegPreset: cameraConfig?.ffmpeg?.preset || 'veryfast',
                    crf: cameraConfig?.ffmpeg?.crf ?? 23
                });

                recorderController = {
                    sessionId: streamClient.sessionId,
                    transportMode: 'websocket-ffmpeg',
                    async stop() {
                        const result = await streamClient.stop({
                            stopTracks: false,
                            closeSocket: true,
                            waitForResult: true
                        });

                        const outputUrl = result?.outputPath || result?.rawPath || result?.hlsPath || '';
                        if (outputUrl && cameraConfig?.output?.shareToCloud !== false) {
                            sendRecordingToMyCloud({
                                outputUrl,
                                outputFile: outputUrl.split('/').pop() || `recording.${cameraConfig?.output?.format || 'mp4'}`,
                                outputBytes: result?.totalBytes || null
                            });
                        }

                        return result;
                    }
                };
            } catch (error) {
                streamClient.close({ stopTracks: false });
                console.warn('WebSocket stream unavailable, falling back to HTTP chunks', error);
                setRecordingStatus('Luồng 2 chưa sẵn sàng, đang chuyển sang Luồng 1');
                recorderController = createHttpRecorder();
                await recorderController.start();
            }
        } else {
            recorderController = createHttpRecorder();
            await recorderController.start();
        }

        return { mediaStream: nextMediaStream, recorderController };
    } catch (error) {
        setRecordingStatus(error.message);
        console.error('Khong bat dau duoc stream', error);
        return { mediaStream, recorderController: null };
    }
}

export async function stopCameraStreamRecording({
    recorderController,
    pendingChunks,
    refreshPendingCount,
    setRecordingStatus
}) {
    playStopRecording();

    try {
        await recorderController.stop();
        setRecordingStatus(pendingChunks > 0 ? 'Đã dừng, sẽ gửi tiếp khi có mạng' : 'Đã dừng và lưu stream');
    } catch (error) {
        setRecordingStatus('Đã dừng, đang chờ đồng bộ lại');
        console.error('Dung stream co loi', error);
    } finally {
        await refreshPendingCount();
    }

    return {
        recorderController: null,
        recording: false,
        stopping: false
    };
}

export function openCameraCaptureGallery(index = 0) {
    return {
        galleryIndex: index,
        galleryOpen: true
    };
}

export function syncCameraRecordingState({
    setRecordingStatus,
    handleSyncProgress
}) {
    setRecordingStatus('Đang kiểm tra đồng bộ');
    syncPendingRecordings({ onProgress: handleSyncProgress }).catch((error) => {
        setRecordingStatus('Chưa thể đồng bộ');
        console.error('Dong bo recording that bai', error);
    });
}
